// Next.js
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';

// Pages
import CompanyPage from 'pages/Company';
import type { Props } from 'pages/Company';

// API
import getFooter from 'api/Footer/getFooter';
import getMenu from 'api/Header/getMenu';
import getCompanyBySlug from 'api/Company/getCompanyBySlug';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import getLatestArticlesByCompany from 'api/Article/getLatestArticlesByCompany';

// Services
import { setPianoConfig } from 'services/Piano';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import setCompanyPageAdConfig from 'services/Ad/config/setCompanyPageAdConfig';

// Interfaces & types
import type { PageAdConfig } from 'interfaces/ads/Ad';
import type { PreviewData } from 'interfaces/PreviewData';
import { PianoPaywallPageType } from '@/app_lib/types/piano/Paywall';

// Utils
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import logApiError from 'utils/log/logApiError';

// Lib
import fallbackData from 'lib/fallbackData/commonData';

const Company: NextPage<Props> = ({ company, latestNews, videos, mostPopularNews, previewKey }: Props) => (
  <CompanyPage
    company={company}
    latestNews={latestNews}
    videos={videos}
    mostPopularNews={mostPopularNews}
    previewKey={previewKey}
  />
);

export default Company;

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug } = context.params as { slug: string };
    const { previewData } = context as { previewData: PreviewData };
    const previewKey = previewData ? previewData.previewKey : null;
    const menu = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const { latestNewsVideos: videos, company } = await getCompanyBySlug(slug, previewData);
    const headData = safe(() => setHeadData(company), fallbackData.headData);
    const { latestNews } = await getLatestArticlesByCompany(slug, 5).catch((error) => {
      logApiError(error as Error, `[pages][company][slug: ${slug}]`);
      return { latestNews: [] };
    });

    // Calculate the date exactly two year ago from the current date.
    const currentDate = new Date();
    const twoYearsAgo = currentDate.setFullYear(currentDate.getFullYear() - 2);
    const newsFromLastTwoYears = latestNews.filter((article) => new Date(article.date) > new Date(twoYearsAgo));

    const mostPopularNews = await getMostPopularPosts('', slug).catch((error) => {
      logApiError(error as Error, `[pages][company][slug: ${slug}]`);
      return [];
    });

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(company), fallbackData.dataLayer);
    const pageAdConfig: PageAdConfig = safe(() => setCompanyPageAdConfig(company), fallbackData.pageAdConfig);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(company),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(
      () => setPianoConfig({ pageType: PianoPaywallPageType.Company, pageData: company }),
      fallbackData.pianoPageConfig,
    );

    if (process.env.FEATURE_FLAG_QUARTR) {
      company.earningReports = company.earningReports || [];
    }

    return {
      props: {
        arcSpanPageConfig,
        company,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        isCompanyPage: true,
        latestNews: newsFromLastTwoYears,
        mostPopularNews,
        pageAdConfig,
        pianoPageConfig,
        previewKey,
        videos,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [],
    };
  }

  return {
    fallback: 'blocking',
    paths: [
      {
        params: { slug: 'walmart' },
      },
    ],
  };
};
